import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/lib/macondo';
import { useI18n } from '@/i18n/I18nProvider';
import { useOverview, useStatement } from '@/api/queries';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import { PageHeading } from '@/components/PageHeading';
import { Card, Section } from '@/components/Card';
import { AgingTable } from '@/components/AgingTable';
import { InvoiceList } from '@/components/InvoiceList';
import { PaymentList } from '@/components/PaymentList';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';

/** `/a/:cid`: what is owed, what is late, what is due next, how old the open balance is, and the latest activity. */
export function OverviewScreen() {
  const { cid = '' } = useParams();
  const { t } = useI18n();
  const q = useOverview(cid);
  // The overview response has no `reliable` flag (only the statement does), so the default statement is
  // fetched quietly to know whether to warn. It is the same query the Statement screen starts with.
  const stmt = useStatement(cid, undefined, undefined);

  return (
    <div className="flex flex-col gap-600" data-screen="OverviewScreen">
      <PageHeading>{t('nav.overview')}</PageHeading>
      {q.isPending ? (
        <LoadingState rows={4} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <Loaded cid={cid} data={q.data} unreliable={stmt.data?.reliable === false} />
      )}
    </div>
  );
}

function Loaded({ cid, data, unreliable }: { cid: string; data: NonNullable<ReturnType<typeof useOverview>['data']>; unreliable: boolean }) {
  const { t, lang } = useI18n();
  const overdue = data.overdue.amount > 0;
  const base = `/a/${encodeURIComponent(cid)}`;
  return (
    <>
      {unreliable && (
        <Alert tone="info" role="note">
          <div className="flex items-start gap-300">
            <Info size={20} aria-hidden="true" className="mt-050 shrink-0" />
            <AlertDescription>{t('statement.unreliable')}</AlertDescription>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-300 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="p-500 sm:col-span-2 lg:col-span-1">
          <p className="text-callout text-typography-secondary">{t('overview.balance')}</p>
          <p className="text-h2 text-typography-primary mt-100 tabular-nums" data-testid="balance">
            {formatMoney(data.balance, lang)}
          </p>
        </Card>
        <Card className={`p-500 ${overdue ? 'border-critical-border bg-critical-surface' : ''}`}>
          <p className="text-callout text-typography-secondary flex items-center gap-100">
            {overdue && <AlertTriangle size={16} aria-hidden="true" className="text-critical-text" />}
            {t('overview.overdue')}
          </p>
          <p className={`text-h3 mt-100 tabular-nums ${overdue ? 'text-critical-text' : 'text-typography-primary'}`} data-testid="overdue">
            {formatMoney(data.overdue, lang)}
          </p>
        </Card>
        <Card className="p-500">
          <p className="text-callout text-typography-secondary">{t('overview.credits')}</p>
          <p className="text-h3 text-typography-primary mt-100 tabular-nums" data-testid="credits">
            {formatMoney(data.unappliedCredits, lang)}
          </p>
        </Card>
      </div>

      <Card className="p-500">
        <h2 className="text-callout text-typography-secondary">{t('overview.nextDue')}</h2>
        {data.nextDue ? (
          <Link
            to={`${base}/invoices/${encodeURIComponent(data.nextDue.invoiceId)}`}
            className="focus-ring text-body text-typography-primary mt-100 inline-block underline"
          >
            {t('overview.nextDueValue', {
              number: data.nextDue.docNumber,
              amount: formatMoney(data.nextDue.balance, lang),
              date: formatDate(data.nextDue.dueDate, lang),
            })}
          </Link>
        ) : (
          <p className="text-body text-typography-primary mt-100">{t('overview.nothingDue')}</p>
        )}
      </Card>

      <Section id="aging-h" title={t('overview.aging')}>
        <Card className="p-500">
          <AgingTable aging={data.aging} currency={data.balance.currency} id="aging-h" />
        </Card>
      </Section>

      <Section
        id="recent-inv-h"
        title={t('overview.recentInvoices')}
        actions={
          <Link to={`${base}/invoices`} className="focus-ring text-callout text-typography-primary inline-flex min-h-[44px] items-center underline">
            {t('overview.viewAll')}
          </Link>
        }
      >
        {data.recentInvoices.length === 0 ? (
          <EmptyState>{t('empty.invoices')}</EmptyState>
        ) : (
          <InvoiceList items={data.recentInvoices} cid={cid} caption={t('overview.recentInvoices')} />
        )}
      </Section>

      <Section
        id="recent-pay-h"
        title={t('overview.recentPayments')}
        actions={
          <Link to={`${base}/payments`} className="focus-ring text-callout text-typography-primary inline-flex min-h-[44px] items-center underline">
            {t('overview.viewAll')}
          </Link>
        }
      >
        {data.recentPayments.length === 0 ? (
          <EmptyState>{t('empty.payments')}</EmptyState>
        ) : (
          <PaymentList items={data.recentPayments} cid={cid} caption={t('overview.recentPayments')} />
        )}
      </Section>

      <p className="text-footnote text-typography-secondary">{t('common.dataAsOf', { time: formatDateTime(data.dataAsOf, lang) })}</p>
    </>
  );
}
